import { on, Show, createSignal, onCleanup, createEffect } from "solid-js";
import { ElementNode, activeElement } from "@solidtv/solid";
import { useFocusStack } from "@solidtv/solid/primitives";
import { useNavigate } from "@solidjs/router";
import { playerState, setPlayerState } from "../overlayState";
import { init, load, play, pause, destroy, getCurrentTime, getVideoDuration, getTimeFormat, seek, setQuality, getQualityOptions, getCaptionOptions, setCaption, isVideoPlaying, state as videoState } from "../video";
import { setGlobalBackground } from "../state";
import theme from "theme";

// @ts-ignore
import playIconRaw from "media-icons/raw/play.svg?raw";
// @ts-ignore
import pauseIconRaw from "media-icons/raw/pause.svg?raw";
// @ts-ignore
import seekForwardIconRaw from "media-icons/raw/seek-forward-10.svg?raw";
// @ts-ignore
import seekBackwardIconRaw from "media-icons/raw/seek-backward-10.svg?raw";
// @ts-ignore
import settingsMenuIconRaw from "media-icons/raw/settings-menu.svg?raw";
// @ts-ignore
import ccIconRaw from "media-icons/raw/closed-captions-on.svg?raw";

const SEEK_STEP = 10;
const CONTROLS_TIMEOUT = 5000;
const POLL_INTERVAL = 500;
const TIZEN_MEDIA_KEYS = ['MediaPlay', 'MediaPause', 'MediaPlayPause', 'MediaFastForward', 'MediaRewind', 'MediaStop'];

export default function PlayerOverlay() {
  const { storeFocus, restoreFocus } = useFocusStack();
  const navigate = useNavigate();
  let viewRef: ElementNode;

  const [currentTime, setCurrentTime] = createSignal(0);
  const [duration, setDuration] = createSignal(0);
  const [isPlaying, setIsPlaying] = createSignal(false);
  const [showControls, setShowControls] = createSignal(true);
  const [controlFocusIndex, setControlFocusIndex] = createSignal(1);
  const [menuType, setMenuType] = createSignal<"none" | "quality" | "captions">("none");
  const [menuOptions, setMenuOptions] = createSignal<any[]>([]);
  const [selectedIndex, setSelectedIndex] = createSignal(0);

  let controlsTimer: ReturnType<typeof setTimeout>;
  let updateInterval: ReturnType<typeof setInterval>;

  // HTML overlay elements
  let overlayEl: HTMLDivElement | null = null;
  let playPauseWrapper: HTMLElement | null = null;
  let playPauseEl: HTMLElement | null = null;
  let seekIndicatorEl: HTMLElement | null = null;
  let trackWrapper: HTMLElement | null = null;
  let progressFillEl: HTMLElement | null = null;
  let timeEl: HTMLElement | null = null;
  let settingsBtnEl: HTMLElement | null = null;
  let ccBtnEl: HTMLElement | null = null;
  let menuEl: HTMLDivElement | null = null;

  function updateControlsVisibility(visible: boolean) {
    setShowControls(visible);
    if (overlayEl) {
      overlayEl.style.opacity = visible ? "1" : "0";
    }
  }

  function registerTizenKeys() {
    // @ts-ignore
    if (typeof tizen !== 'undefined' && tizen.tvinputdevice && tizen.tvinputdevice.registerKeyBatch) {
      try {
        // @ts-ignore
        tizen.tvinputdevice.registerKeyBatch(TIZEN_MEDIA_KEYS);
      } catch (e) {
        console.warn("[Player] Failed to register Tizen media keys", e);
      }
    }
  }

  function unregisterTizenKeys() {
    // @ts-ignore
    if (typeof tizen !== 'undefined' && tizen.tvinputdevice && tizen.tvinputdevice.unregisterKeyBatch) {
      try {
        // @ts-ignore
        tizen.tvinputdevice.unregisterKeyBatch(TIZEN_MEDIA_KEYS);
      } catch (e) {
        console.warn("[Player] Failed to unregister Tizen media keys", e);
      }
    }
  }

  const handleMediaKeyDown = (e: KeyboardEvent) => {
    if (!playerState()) return;
    const key = e.key || "";

    if (TIZEN_MEDIA_KEYS.includes(key)) {
      e.stopPropagation();
      e.preventDefault();

      if (key === "MediaPlay") {
        if (!isPlaying()) togglePlayPause();
        resetControlsTimer();
      } else if (key === "MediaPause") {
        if (isPlaying()) togglePlayPause();
        resetControlsTimer();
      } else if (key === "MediaPlayPause") {
        togglePlayPause();
        resetControlsTimer();
      } else if (key === "MediaFastForward") {
        seekForward();
      } else if (key === "MediaRewind") {
        seekBackward();
      } else if (key === "MediaStop") {
        close();
      }
    }
  };

  function resetControlsTimer() {
    updateControlsVisibility(true);
    clearTimeout(controlsTimer);
    controlsTimer = setTimeout(() => {
      if (menuType() === "none") {
        updateControlsVisibility(false);
      }
    }, CONTROLS_TIMEOUT);
  }

  function setUIPlaying(playing: boolean) {
    if (isPlaying() !== playing) setIsPlaying(playing);
    if (playPauseEl) {
       playPauseEl.innerHTML = playing ? pauseIconRaw : playIconRaw;
       if (playPauseEl.firstChild instanceof SVGSVGElement) {
         (playPauseEl.firstChild as SVGSVGElement).style.width = "100%";
         (playPauseEl.firstChild as SVGSVGElement).style.height = "100%";
         (playPauseEl.firstChild as SVGSVGElement).style.fill = "#fff";
       }
    }
  }

  function moveFocus(idx: number) {
    setControlFocusIndex(idx);
    if (playPauseWrapper) playPauseWrapper.style.border = idx === 1 ? "4px solid #fff" : "4px solid transparent";
    if (settingsBtnEl) settingsBtnEl.style.border = idx === 2 ? "4px solid #fff" : "4px solid transparent";
    if (ccBtnEl) ccBtnEl.style.border = idx === 3 ? "4px solid #fff" : "4px solid transparent";
    if (trackWrapper) trackWrapper.style.transform = idx === 0 ? "scaleY(2.0)" : "scaleY(1)";
  }

  function renderMenu() {
    if (!menuEl) return;
    const type = menuType();
    const options = menuOptions();
    const currentIdx = selectedIndex();

    if (type === "none") {
      menuEl.style.opacity = "0";
      return;
    }

    menuEl.style.opacity = "1";
    menuEl.innerHTML = `<div style="font-weight:bold;margin-bottom:10px;border-bottom:1px solid #555;padding-bottom:5px;">${type === "quality" ? "Video Quality" : "Captions"}</div>`;

    options.forEach((opt, idx) => {
      const item = document.createElement("div");
      item.style.cssText = "padding:8px 15px;border-radius:4px;display:flex;align-items:center;justify-content:space-between;gap:20px;";

      if (idx === currentIdx) {
        item.style.background = "#4A7BD0";
        item.style.fontWeight = "bold";
      } else {
        item.style.color = "rgba(255,255,255,0.7)";
      }

      const label = document.createElement("span");
      label.textContent = opt.label;
      item.appendChild(label);

      if (opt.active) {
        const check = document.createElement("span");
        check.style.color = "#4ADE80";
        check.innerHTML = "&#10003;"; // Checkmark
        item.appendChild(check);
      }

      menuEl!.appendChild(item);
    });
  }

  function formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return "--:--";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    return `${m}:${s.toString().padStart(2, "0")}`;
  }

  function buildControlsOverlay(stateObj: any) {
    if (overlayEl) return;

    overlayEl = document.createElement("div");
    overlayEl.style.cssText =
      "position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:3000;pointer-events:none;display:flex;flex-direction:column;justify-content:space-between;transition:opacity 0.3s;font-family:Roboto,sans-serif;";

    const topBar = document.createElement("div");
    topBar.style.cssText = "background:linear-gradient(to bottom,rgba(0,0,0,0.7),transparent);padding:40px 60px;";

    const titleEl = document.createElement("div");
    titleEl.style.cssText = "color:#fff;font-size:32px;font-weight:bold;";
    titleEl.textContent = stateObj.title || "";
    topBar.appendChild(titleEl);

    if (stateObj.isLive) {
      const badge = document.createElement("span");
      badge.style.cssText =
        "display:inline-block;background:#e53e3e;color:#fff;padding:2px 12px;border-radius:4px;font-size:18px;font-weight:bold;margin-top:10px;";
      badge.textContent = "LIVE";
      topBar.appendChild(badge);
    }
    overlayEl.appendChild(topBar);

    const spacer = document.createElement("div");
    spacer.style.flex = "1";
    overlayEl.appendChild(spacer);

    const bottomBar = document.createElement("div");
    bottomBar.style.cssText = "background:linear-gradient(to top,rgba(0,0,0,0.7),transparent);padding:40px 60px;";

    const buttonsRow = document.createElement("div");
    buttonsRow.style.cssText = "display:flex;align-items:center;gap:20px;margin-bottom:15px;";

    playPauseWrapper = document.createElement("div");
    playPauseWrapper.style.cssText = "display:flex;align-items:center;justify-content:center;width:64px;height:64px;border-radius:32px;border:4px solid transparent;transition:border 0.2s;";

    playPauseEl = document.createElement("div");
    playPauseEl.style.cssText = "color:#fff;width:32px;height:32px;display:flex;align-items:center;justify-content:center;";

    playPauseWrapper.appendChild(playPauseEl);
    buttonsRow.appendChild(playPauseWrapper);

    const spacerBtn = document.createElement("div");
    spacerBtn.style.flex = "1";
    buttonsRow.appendChild(spacerBtn);

    if (!stateObj.isLive) {
      settingsBtnEl = document.createElement("div");
      settingsBtnEl.style.cssText = "display:flex;align-items:center;justify-content:center;width:64px;height:64px;border-radius:32px;border:4px solid transparent;transition:border 0.2s;";
      settingsBtnEl.innerHTML = `<div style="width:32px;height:32px;fill:#fff; color:#fff;">${settingsMenuIconRaw}</div>`;
      buttonsRow.appendChild(settingsBtnEl);

      ccBtnEl = document.createElement("div");
      ccBtnEl.style.cssText = "display:flex;align-items:center;justify-content:center;width:64px;height:64px;border-radius:32px;border:4px solid transparent;transition:border 0.2s;";
      ccBtnEl.innerHTML = `<div style="width:32px;height:32px;fill:#fff; color:#fff;">${ccIconRaw}</div>`;
      buttonsRow.appendChild(ccBtnEl);
    }

    bottomBar.appendChild(buttonsRow);

    seekIndicatorEl = document.createElement("div");
    seekIndicatorEl.style.cssText =
      "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);color:#fff;width:80px;height:80px;opacity:0;transition:opacity 0.2s;pointer-events:none;";
    overlayEl.appendChild(seekIndicatorEl);

    if (!stateObj.isLive) {
      const row = document.createElement("div");
      row.style.cssText = "display:flex;align-items:center;gap:20px;";

      trackWrapper = document.createElement("div");
      trackWrapper.style.cssText = "flex:1;padding:10px 0;transition:transform 0.2s;transform-origin:center;";

      const track = document.createElement("div");
      track.style.cssText = "width:100%;height:8px;background:rgba(255,255,255,0.2);border-radius:4px;overflow:hidden;";

      progressFillEl = document.createElement("div");
      progressFillEl.style.cssText =
        "height:100%;background:#4A7BD0;border-radius:4px;width:0%;transition:width 0.4s linear;";
      track.appendChild(progressFillEl);
      trackWrapper.appendChild(track);
      row.appendChild(trackWrapper);

      timeEl = document.createElement("div");
      timeEl.style.cssText = "color:rgba(255,255,255,0.8);font-size:24px;white-space:nowrap;";
      timeEl.textContent = "--:-- / --:--";
      row.appendChild(timeEl);

      bottomBar.appendChild(row);
    } else {
      const liveRow = document.createElement("div");
      liveRow.style.cssText = "display:flex;align-items:center;justify-content:space-between;";

      const liveText = document.createElement("div");
      liveText.style.cssText = "color:rgba(255,255,255,0.8);font-size:24px;";
      liveText.textContent = "Live broadcast";
      liveRow.appendChild(liveText);

      bottomBar.appendChild(liveRow);
    }

    menuEl = document.createElement("div");
    menuEl.style.cssText =
      "position:absolute;top:20%;right:60px;background:rgba(0,0,0,0.85);padding:20px;border-radius:8px;color:#fff;display:flex;flex-direction:column;gap:15px;opacity:0;transition:opacity 0.2s;";
    overlayEl.appendChild(menuEl);

    overlayEl.appendChild(bottomBar);
    document.body.appendChild(overlayEl);

    // Initial setup
    setUIPlaying(isVideoPlaying());
    moveFocus(1);
    renderMenu();
  }

  createEffect(
    on(
      playerState,
      (stateObj) => {
        if (stateObj) {
          storeFocus(activeElement() as ElementNode);
          setGlobalBackground("#000000");
          buildControlsOverlay(stateObj);

          registerTizenKeys();
          document.addEventListener("keydown", handleMediaKeyDown);

          init(null)
            .then(() => load({ streamUrl: stateObj.manifestUrl, tracks: stateObj.tracks }))
            .then(() => {
              play();
              setUIPlaying(true);
              resetControlsTimer();
            })
            .catch((e: unknown) => console.error("[Player] Playback error:", e));

          updateInterval = setInterval(() => {
            try {
              const cur = getCurrentTime() || 0;
              const dur = getVideoDuration() || 0;
              setCurrentTime(cur);
              setDuration(dur);
              setUIPlaying(isVideoPlaying());

              if (!stateObj.isLive) {
                const pct = dur > 0 && isFinite(dur) ? (cur / dur) * 100 : 0;
                if (progressFillEl) progressFillEl.style.width = `${pct}%`;
                if (timeEl) timeEl.textContent = `${formatTime(cur)} / ${formatTime(dur)}`;
              }
            } catch {
              // video not ready
            }
          }, POLL_INTERVAL);

          queueMicrotask(() => viewRef?.setFocus());
        }
      },
      { defer: true }
    )
  );

  onCleanup(() => {
    clearInterval(updateInterval);
    clearTimeout(controlsTimer);
    unregisterTizenKeys();
    document.removeEventListener("keydown", handleMediaKeyDown);
    if (overlayEl) {
      overlayEl.remove();
      overlayEl = null;
    }
  });

  function flashSeekIcon(iconRaw: string) {
    if (!seekIndicatorEl) return;
    seekIndicatorEl.innerHTML = iconRaw;
    if (seekIndicatorEl.firstChild instanceof SVGSVGElement) {
       (seekIndicatorEl.firstChild as SVGSVGElement).style.width = "100%";
       (seekIndicatorEl.firstChild as SVGSVGElement).style.height = "100%";
       (seekIndicatorEl.firstChild as SVGSVGElement).style.fill = "#fff";
    }
    seekIndicatorEl.style.opacity = "1";
    setTimeout(() => {
      if (seekIndicatorEl) seekIndicatorEl.style.opacity = "0";
    }, 500);
  }

  function togglePlayPause() {
    if (isPlaying()) {
      pause();
    } else {
      play();
    }
    setUIPlaying(isVideoPlaying());
    resetControlsTimer();
  }

  function openMenu(type: "quality" | "captions") {
    const options = type === "quality" ? getQualityOptions() : getCaptionOptions();
    if (options.length === 0) return; // Prevent empty menus
    setMenuOptions(options);
    const activeIdx = options.findIndex(o => o.active);
    setSelectedIndex(activeIdx !== -1 ? activeIdx : 0);
    setMenuType(type);
    updateControlsVisibility(true);
    renderMenu();
  }

  function seekForward() {
    if (playerState()?.isLive) return;
    const target = Math.min((getCurrentTime() || 0) + SEEK_STEP, getVideoDuration() || 0);
    seek(target);
    flashSeekIcon(seekForwardIconRaw);
    resetControlsTimer();
  }

  function seekBackward() {
    if (playerState()?.isLive) return;
    const target = Math.max((getCurrentTime() || 0) - SEEK_STEP, 0);
    seek(target);
    flashSeekIcon(seekBackwardIconRaw);
    resetControlsTimer();
  }

  function close() {
    if (menuType() !== "none") {
      setMenuType("none");
      renderMenu();
      resetControlsTimer();
      return true;
    }

    if (overlayEl) {
      overlayEl.remove();
      overlayEl = null;
    }
    clearInterval(updateInterval);
    clearTimeout(controlsTimer);
    unregisterTizenKeys();
    document.removeEventListener("keydown", handleMediaKeyDown);

    destroy().catch((e: unknown) =>
      console.warn("[JW] Player destroy error:", e)
    );
    setPlayerState(null);
    restoreFocus();

    document.getElementsByTagName("canvas")[0]?.focus();

    navigate("/home");
    return true;
  }

  return (
    <Show when={playerState()}>
      <view
        ref={viewRef!}
        autofocus
        width={theme.layout.screenW}
        height={1080}
        zIndex={300}
        color={0x00000000}
        onBack={close}
        shader={{ type: "holePunch" }}
        onBackspace={close}
        onEscape={close}
        onEnter={() => {
          if (!showControls()) {
             resetControlsTimer();
             return true;
          }
          if (menuType() !== "none") {
            const opt = menuOptions()[selectedIndex()];
            if (menuType() === "quality") setQuality(opt.id);
            if (menuType() === "captions") setCaption(opt.id);
            setMenuType("none");
            renderMenu();
            resetControlsTimer();
          } else {
            const idx = controlFocusIndex();
            if (idx === 1) togglePlayPause();
            else if (idx === 2) openMenu("quality");
            else if (idx === 3) openMenu("captions");
            else resetControlsTimer();
          }
          return true;
        }}
        onPlay={() => { if (!isPlaying()) togglePlayPause(); resetControlsTimer(); return true; }}
        onPause={() => { if (isPlaying()) togglePlayPause(); resetControlsTimer(); return true; }}
        onPlayPause={() => { togglePlayPause(); return true; }}
        onLeft={() => {
          if (!showControls()) {
             seekBackward();
             resetControlsTimer();
             return true;
          }
          if (menuType() !== "none") return true;
          const idx = controlFocusIndex();
          if (idx === 0) {
            seekBackward();
          } else if (idx > 1) {
            moveFocus(idx - 1);
          }
          resetControlsTimer();
          return true;
        }}
        onRight={() => {
          if (!showControls()) {
             seekForward();
             resetControlsTimer();
             return true;
          }
          if (menuType() !== "none") return true;
          const idx = controlFocusIndex();
          if (idx === 0) {
            seekForward();
          } else if (idx < 3) {
            if (playerState()?.isLive) return true;
            moveFocus(idx + 1);
          }
          resetControlsTimer();
          return true;
        }}
        onFastForward={() => { seekForward(); return true; }}
        onRewind={() => { seekBackward(); return true; }}
        onUp={() => {
          if (!showControls()) {
             resetControlsTimer();
             return true;
          }
          if (menuType() === "none") {
            if (controlFocusIndex() !== 0 && !playerState()?.isLive) {
              moveFocus(0);
            }
          } else {
            setSelectedIndex(i => Math.max(0, i - 1));
            renderMenu();
          }
          resetControlsTimer();
          return true;
        }}
        onDown={() => {
          if (!showControls()) {
             resetControlsTimer();
             return true;
          }
          if (menuType() === "none") {
            if (controlFocusIndex() === 0) {
              moveFocus(1);
            }
          } else {
            setSelectedIndex(i => Math.min(menuOptions().length - 1, i + 1));
            renderMenu();
          }
          resetControlsTimer();
          return true;
        }}
      />
    </Show>
  );
}

